/* eslint-disable react/jsx-props-no-spreading */
import React, { Fragment } from 'react';
import {
  Button,
  Col,
  Form,
  FormControl,
  OverlayTrigger,
  Row,
  Tooltip,
} from 'react-bootstrap';
import {
  BsCheckCircle,
  BsFillFileEarmarkFill,
  BsInfoCircle,
} from 'react-icons/bs';

import classNames from 'classnames';
import { useField, useFormikContext } from 'formik';
import * as Yup from 'yup';

export const FileUploadFileTooBig = () =>
  Yup.mixed().test('is-file-too-big', 'File exceeds 50MB', (value) => {
    let valid = true;
    if (value) {
      const fileArr = Array.from(value);
      fileArr.forEach((file) => {
        const size = file.size / 1024 / 1024;
        if (size > 50) {
          valid = false;
        }
      });
    }
    return valid;
  });

export const FileUploadField = ({ name, hidden = false, multiple = false }) => {
  const [_, meta, helpers] = useField(name);
  const { touched } = useFormikContext();
  const { error } = meta;
  const isValid = touched.attachments && !error;
  const isInvalid = touched.attachments && error;
  return (
    <>
      <FormControl
        name={name}
        type="file"
        onChange={(event) => {
          helpers.setValue(event.currentTarget.files);
        }}
        style={hidden ? { display: 'none' } : {}}
        multiple={multiple}
        {...{ isInvalid, isValid }}
      />
      {!hidden ? (
        <FormControl.Feedback type="invalid">{error}</FormControl.Feedback>
      ) : null}
    </>
  );
};

export class FieldBuilder {
  constructor(context) {
    this.context = context;
  }

  renderCharField(label, accessor, labelProps = {}, fieldProps = {}) {
    const { handleChange, handleBlur, values, touched, errors } = this.context;
    return (
      <Form.Group
        as={Row}
        className="mb-1"
        controlId={`form.${accessor}`}
        key={accessor}
      >
        <Form.Label column sm="4" {...labelProps}>
          {label}
        </Form.Label>
        <Col sm="8">
          <Form.Control
            type="text"
            name={accessor}
            value={values[accessor]}
            onChange={handleChange}
            onBlur={handleBlur}
            isValid={touched[accessor] && !errors[accessor]}
            isInvalid={touched[accessor] && errors[accessor]}
            {...fieldProps}
          />
        </Col>
      </Form.Group>
    );
  }

  renderSelectField(
    label,
    accessor,
    query,
    blankValue = undefined,
    labelProps = {},
    selectProps = {}
  ) {
    const { handleChange, handleBlur, values, touched, errors } = this.context;
    return (
      <Form.Group
        as={Row}
        className="mb-1"
        controlId={`form.${accessor}`}
        key={accessor}
      >
        <Form.Label column sm="4" {...labelProps}>
          {label}
        </Form.Label>
        <Col sm="8">
          <Form.Select
            name={accessor}
            value={values[accessor]}
            onChange={handleChange}
            onBlur={handleBlur}
            isValid={touched[accessor] && !errors[accessor]}
            isInvalid={touched[accessor] && errors[accessor]}
            disabled={query.isLoading || query.isFetching}
            {...selectProps}
          >
            <option value="">{blankValue || `Select ${label}`}</option>
            {!query.isUninitialized &&
            !query.isLoading &&
            !query.isFetching &&
            !query.isError &&
            query.data.length > 0 ? (
              <>
                {query.data.map((item) => (
                  <option key={`${accessor}-${item.value}`} value={item.value}>
                    {item.name}
                  </option>
                ))}
              </>
            ) : null}
          </Form.Select>
        </Col>
      </Form.Group>
    );
  }

  renderBooleanField(label, accessor) {
    const { handleChange, handleBlur, values, touched, errors } = this.context;
    return (
      <Form.Group
        as={Row}
        className="mb-1"
        controlId={`form.${accessor}`}
        key={accessor}
      >
        <Form.Label column sm="4">
          {label}
        </Form.Label>
        <Col sm="8" className="form-label col-form-label">
          <Form.Check
            type="checkbox"
            name={accessor}
            checked={values[accessor]}
            value={values[accessor]}
            onChange={handleChange}
            onBlur={handleBlur}
            isValid={touched[accessor] && !errors[accessor]}
            isInvalid={touched[accessor] && errors[accessor]}
          />
        </Col>
      </Form.Group>
    );
  }

  renderFileView(accessor) {
    const { values, errors } = this.context;
    return values[accessor] && values[accessor].length !== 0 ? (
      <Row>
        <Col className="form-label col-form-label" sm={{ span: 6, offset: 6 }}>
          {Array.from(values[accessor]).map((item, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <p className="normalFontSize" key={item.name + index}>
              <BsFillFileEarmarkFill className="me-1" />
              {item.name}{' '}
              {!errors[accessor] ? (
                <BsCheckCircle className="textGreen" />
              ) : null}
            </p>
          ))}
          {errors[accessor] ? (
            <p className="normalFontSize textRed">{errors[accessor]}</p>
          ) : null}
          <p className="normalFontSize textRed">
            Press “save end user” to submit.
          </p>
        </Col>
      </Row>
    ) : null;
  }

  renderDocumentField(
    label,
    accessor,
    multiple = false,
    info = null,
    requiredNew = false
  ) {
    const { initialValues, values, errors, touched } = this.context;
    let showFiles = true;
    let replacement = false;
    if (initialValues) {
      replacement =
        Object.prototype.hasOwnProperty.call(initialValues, accessor) &&
        initialValues[accessor];
    }
    if (replacement) {
      showFiles = replacement && initialValues[accessor] !== values[accessor];
    }

    function renderButtonText() {
      if ((multiple && replacement) || requiredNew) {
        return <>Add new</>;
      }
      if (multiple) {
        return <>Add</>;
      }
      return replacement ? <>Replace</> : <>Add</>;
    }
    const LocalFieldLabel = () =>
      info ? (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`tooltip-${label}`} style={{ position: 'fixed' }}>
              {info}
            </Tooltip>
          }
        >
          <span>
            {label}{' '}
            <span className="textGreen">
              <BsInfoCircle size={17} />
            </span>
          </span>
        </OverlayTrigger>
      ) : (
        label
      );
    const error = errors[accessor];
    const isValid = touched[accessor] && !errors[accessor];

    return (
      <Fragment key={accessor}>
        <Form.Group
          as={Row}
          className="mb-1"
          controlId={`form.${accessor}`}
          key={accessor}
        >
          <Col
            className={classNames('form-label col-form-label', {
              textRed: Boolean(error),
              textGreen: isValid,
            })}
            sm="6"
          >
            <LocalFieldLabel />
          </Col>
          <Form.Label column sm="6">
            <Button as={Col} md={9} variant="didxl" size="sm">
              {renderButtonText()}
            </Button>
          </Form.Label>
          <FileUploadField name={accessor} hidden multiple={multiple} />
        </Form.Group>
        {showFiles ? this.renderFileView(accessor) : null}
      </Fragment>
    );
  }
}
